import { useEffect, useRef, useState } from "react"
import maplibregl from "maplibre-gl"
import "maplibre-gl/dist/maplibre-gl.css"
import circle from "@turf/circle"
import bbox from "@turf/bbox"
import type { Feature, Polygon, Geometry } from "geojson"

import { Label } from "@/components/ui/label"

interface Props {
  center: [number, number] | null  // [lng, lat]
  radiusKm: number | null
  boundaryGeoJSON: object | null
  onChange: (center: [number, number], radiusKm: number) => void
}

const DEFAULT_CENTER: [number, number] = [-9.14, 38.72]  // Lisbon
const DEFAULT_ZOOM = 5

const OSM_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    },
  },
  layers: [{ id: "osm", type: "raster", source: "osm" }],
}

export function RegionBoundaryEditor({
  center,
  radiusKm,
  boundaryGeoJSON,
  onChange,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const markerRef = useRef<maplibregl.Marker | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange  // always current without re-running effects

  const [localCenter, setLocalCenter] = useState<[number, number] | null>(center)
  const [localRadius, setLocalRadius] = useState<number>(radiusKm ?? 10)

  // Initialize map once
  useEffect(() => {
    if (!containerRef.current) return

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: OSM_STYLE,
      center: center ?? DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    })

    map.on("load", () => {
      // Circle source — fill, solid outer ring, inner dashed ring
      map.addSource("boundary-circle", {
        type: "geojson",
        data: { type: "FeatureCollection" as const, features: [] },
      })
      map.addLayer({
        id: "boundary-circle-fill",
        type: "fill",
        source: "boundary-circle",
        paint: { "fill-color": "#3b82f6", "fill-opacity": 0.18 },
      })
      map.addLayer({
        id: "boundary-circle-outline",
        type: "line",
        source: "boundary-circle",
        paint: { "line-color": "#2563eb", "line-width": 2.5 },
      })
      map.addLayer({
        id: "boundary-circle-inner",
        type: "line",
        source: "boundary-circle",
        paint: {
          "line-color": "rgba(59,130,246,0.5)",
          "line-width": 1,
          "line-dasharray": [4, 3],
          "line-gap-width": 8,
        },
      })

      // Render initial circle and fit map to it
      if (center && radiusKm) {
        const feature = updateCircle(map, center, radiusKm)
        map.fitBounds(bbox(feature) as [number, number, number, number], {
          padding: 32,
          animate: false,
        })
      }

      // If there's a saved boundary but no editable center yet, show it for context
      if (boundaryGeoJSON && !center) {
        map.addSource("saved-boundary", {
          type: "geojson",
          data: boundaryGeoJSON as Geometry,
        })
        map.addLayer({
          id: "saved-boundary-fill",
          type: "fill",
          source: "saved-boundary",
          paint: { "fill-color": "#6b7280", "fill-opacity": 0.1 },
        })
        map.addLayer({
          id: "saved-boundary-outline",
          type: "line",
          source: "saved-boundary",
          paint: {
            "line-color": "#6b7280",
            "line-width": 1,
            "line-dasharray": [4, 2],
          },
        })
      }

      // Place marker if center already exists
      if (center) {
        markerRef.current = new maplibregl.Marker({ color: "#2563eb" })
          .setLngLat(center)
          .addTo(map)
      }
    })

    // Click to set/move center
    map.on("click", (e) => {
      const newCenter: [number, number] = [e.lngLat.lng, e.lngLat.lat]
      setLocalCenter(newCenter)

      if (markerRef.current) {
        markerRef.current.setLngLat(newCenter)
      } else {
        markerRef.current = new maplibregl.Marker({ color: "#2563eb" })
          .setLngLat(newCenter)
          .addTo(map)
      }
    })

    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Update circle preview and notify parent whenever localCenter or localRadius changes
  // (does not re-fit bounds — avoids map jumping during slider interaction)
  useEffect(() => {
    const map = mapRef.current
    if (!map?.isStyleLoaded() || !localCenter) return

    updateCircle(map, localCenter, localRadius)
    onChangeRef.current(localCenter, localRadius)
  }, [localCenter, localRadius])

  return (
    <div className="grid gap-3">
      <div ref={containerRef} className="h-64 w-full overflow-hidden rounded-lg border" />

      <div className="grid gap-1.5">
        <Label>
          Radius:{" "}
          <span className="font-normal text-muted-foreground">{localRadius} km</span>
        </Label>
        <input
          type="range"
          min={1}
          max={50}
          step={0.5}
          value={localRadius}
          onChange={(e) => setLocalRadius(Number(e.target.value))}
          className="w-full"
        />
      </div>

      <p className="text-xs text-muted-foreground">
        {localCenter
          ? `Center: ${localCenter[1].toFixed(4)}, ${localCenter[0].toFixed(4)} · Radius: ${localRadius} km`
          : "Click on the map to set the region center"}
      </p>
    </div>
  )
}

// Returns the created feature so callers can use it for fitBounds
function updateCircle(
  map: maplibregl.Map,
  center: [number, number],
  radiusKm: number,
): Feature<Polygon> {
  const feature = circle(center, radiusKm, { units: "kilometers", steps: 64 })
  const source = map.getSource("boundary-circle") as
    | maplibregl.GeoJSONSource
    | undefined
  source?.setData(feature)
  return feature
}
